import { TWEEN_ONCOMPLETE } from "Data/Constants";
import { Apple } from "GameObjects/StoryGame/ch7/Apple";
import { AppleTarget } from "GameObjects/StoryGame/ch7/AppleTarget";
import { Sheep } from "GameObjects/StoryGame/ch7/Sheep";
import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Circ, Expo, Linear, Sine, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { lerp } from "html-living-framework/lib/Utils/MathUtils";
import { floatBetween } from "html-living-framework/lib/Utils/Random";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list } from "Utils/Reflection";
// @ts-ignore
import {BezierPlugin} from "gsap/BezierPlugin";

import { ch7_p07 } from "../../../layouts/ch7_p07/ch7_p07";

const dontTreeShake = [BezierPlugin];

export class Chapter7Page7 extends AbstractStoryState implements ReturnType<typeof ch7_p07> {

    readonly bg = this.addChild(new SceneSprite());
    readonly gateOpen = this.addChild(new SceneSprite());
    readonly table = this.addChild(new SceneSprite());
    readonly sheepSizeReference = this.addChild(new SceneSprite());
    readonly sheep = [
        this.addChild(new Sheep("Tan-Sheep")),
        this.addChild(new Sheep("Grey-Sheep")),
        this.addChild(new Sheep("Spotted-Sheep")),
    ];
    readonly penFront = this.addChild(new SceneSprite());
    readonly gateClosed = this.addChild(new SceneSprite());
    readonly holly = this.addChild(new SceneSprite());
    readonly satchelBack = this.addChild(new SceneSprite());
    readonly apple = [
        this.addChild(new Apple()),
        this.addChild(new Apple()),
        this.addChild(new Apple()),
        this.addChild(new Apple()),
    ];
    readonly satchelFront = this.addChild(new SceneSprite());

    readonly frameLeft = this.addChild(SceneSprite.withPrefix("ch7_p07/"));
    readonly frameRight = this.addChild(SceneSprite.withPrefix("ch7_p07/"));
    readonly linesLeft = this.addChild(SceneSprite.withPrefix("ch7_p07/"));
    readonly linesRight = this.addChild(SceneSprite.withPrefix("ch7_p07/"));

    readonly appleTarget = list(() => this.addChild(new AppleTarget()));

    sheepMinY: number = 0;
    sheepMaxY: number = 0;
    sheepMinScale: number = 1;
    sheepMaxScale: number = 1;

    public init(pSize: PIXI.Point, pData: any) {
        // exported layouts from Animate CC
        ch7_p07.call(this);
        this.frameLeft.visible = false;
        this.frameRight.visible = false;
        this.apple[0].angle = 0;
        this.apple[1].angle = -5;
        this.apple[2].angle = -15;
        this.apple[3].angle = -30;

        this.sheepMinY = this.sheepSizeReference.y;
        this.sheepMaxY = this.sheep[2].position.y;

        this.sheepMaxScale = this.sheep[2].scale.y;
        this.sheepMinScale = (this.sheepSizeReference.height / this.sheep[2].height) * this.sheep[2].scale.y;

        this.sheepSizeReference.visible = false;
        this.gateClosed.visible = false;

        // super.init calls onResize
        super.init(pSize, pData);

        this.updateSheepScale();

        this._subscriptions.push([
            PubSub.subscribe('drop', this.onAppleDropped.bind(this)),
            PubSub.subscribe(HLF.DRAG_BEGIN, this.onDragBegin.bind(this)),
            PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onDragBegin.bind(this)),
        ]);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        this.updateSheepScale();
        this.sheep.forEach(s => s.update(pDeltaTime));
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
        [this.satchelBack, ...this.apple, this.satchelFront].forEach(thing => {
            TweenLite.from(thing, 1.5, { pixi: { y: thing.y + 200 } }).delay(4.5);
        });
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();
        this.holly.FadeOut();
    }

    protected walkToAppleAndEatIt(pSheep: Sheep, pApple: Apple, pAppleTarget: AppleTarget) {
        pApple.visuals.position.set(0, 0);
        pApple.visuals.scale.set(1);
        pApple.deselect();
        const applePrevPos = pApple.position.clone();
        const applePrevScale = pApple.scale.clone();

        const yPercent = (pAppleTarget.y - this.sheepMinY) / (this.sheepMaxY - this.sheepMinY);

        this.apple.forEach(a => {
            a.interactive = false; // disable all apples
        });

        // pAppleTarget.isBusy = true;

        new TimelineLite().to(pApple, 0.5, {
            pixi: {
                scale: lerp(0.1, 0.25, yPercent),
            }
        }).to(pApple.position, 1.5, {
            bezier: {
                type: "soft",
                values: [
                    {x: (pAppleTarget.x + pApple.x) / 2, y: pAppleTarget.y - 300},
                    {x: pAppleTarget.x, y: pAppleTarget.y},
                ],
            },
            ease: Expo.easeOut,
        }, 0)
        .call(()=>{
            this.setChildIndex(pApple, this.getChildIndex(pSheep) - 1);
        });

        pSheep.scale.x = Math.abs(pSheep.scale.x); // face sheep left

        const sheepHeadOffset = lerp(50, 100, yPercent);
        const x = pAppleTarget.x + sheepHeadOffset;
        const y = pAppleTarget.y;

        pSheep.WalkTo({x, y}, () => {
            TweenLite.delayedCall(1.5, () => {
                pApple.Eat(0.5);
            });
            TweenLite.delayedCall(2, () => {
                pApple.Eat(1);
            });
            pSheep.Eat(() => {
                pApple.position.set(applePrevPos.x, applePrevPos.y);
                pApple.scale.set(applePrevScale.x, applePrevScale.y);
                this.apple.forEach(a => {
                    a.interactive = true; // enable all apples
                });
                pApple.Eat(0);
                // pAppleTarget.isBusy = false;
                this.setChildIndex(pApple, this.getChildIndex(this.satchelFront) - 1);

                // shuffle sheep around if in pen
                if (pSheep.y <= this.sheepMinY) {
                    for (let i = 0; i<this.sheep.length; i++) {
                        if (this.sheep[i].y <= this.sheepMinY) {

                            const walkDist = (((this.sheep.length - i) / this.sheep.length) * 50);
                            const destX = this.sheep[i].x - walkDist;
                            TweenLite.delayedCall((11 / 3) - 2.75, ()=>{
                                this.sheep[i].WalkTo({x: destX, y: this.sheep[i].y - 4}, ()=>{
                                    if (this.sheep[i] === pSheep) {
                                        this.checkIfAllSheepInPen();
                                    }
                                });
                            });
                        }
                    }
                }

            });
        });
    }

    protected onDragBegin(pTopic: string, pData: Apple) {
        console.log(pTopic, pData);

        let sheepToMove;
        for (let i = 0; i < this.sheep.length; i++) {
            // skip sheep that are already in the pen
            if (this.sheep[i].y <= this.sheepMinY) {
                continue;
            }
            if (sheepToMove === undefined) {
                sheepToMove = this.sheep[i];
            }
            else if (this.sheep[i].didMove && !sheepToMove.didMove) { // prefer sheep that already moved
                sheepToMove = this.sheep[i];
            }
        }

        if (sheepToMove !== undefined) {

            let targetToMoveTo;
            const minsheepmovedist = 200;
            for (let i = 0; i < this.appleTarget.length; i++) {
                if (targetToMoveTo === undefined) {
                    // sheep only walk left
                    if (this.appleTarget[i].x < sheepToMove.x - minsheepmovedist) {
                        targetToMoveTo = this.appleTarget[i];
                    }
                }
                else { // we already have a valid target, but maybe this one is closer to the sheep?
                    // sheep only walk left
                    if (this.appleTarget[i].x < sheepToMove.x - minsheepmovedist) {
                        if (targetToMoveTo.x < this.appleTarget[i].x) {
                            targetToMoveTo = this.appleTarget[i];
                        }
                    }
                }
            }
            if (sheepToMove && targetToMoveTo) {
                this.walkToAppleAndEatIt(sheepToMove, pData, targetToMoveTo);
            }
        }
    }

    protected onAppleDropped(pTopic: 'drop', pData: { item: Apple, target: AppleTarget }) {
        console.log(pTopic, pData);
    }

    protected checkIfAllSheepInPen() {
        if (!this.sheep.find(s => s.y > this.sheepMinY)) {
            this.gateOpen.FadeOut();
            this.gateClosed.FadeIn();
            this.apple.forEach(a => a.interactive = false);
            TweenLite.delayedCall(3, () => {
                this.gotoNextState({ transition: "left" });
            });
        }
    }
    protected updateSheepScale() {
        this.sheep.forEach(s => {
            const yPercent = (s.y - this.sheepMinY) / (this.sheepMaxY - this.sheepMinY);
            const newScale = lerp(this.sheepMinScale, this.sheepMaxScale, yPercent);
            s.scale.set(newScale * Math.sign(s.scale.x), newScale);
        });
    }
}